import { useEffect, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { JobListRow, InvoiceCreateResponse } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { FormDrawer } from "@/staff/jobs/FormDrawer";
import { CreateInvoiceDrawer } from "@/staff/jobs/CreateInvoiceDrawer";

/**
 * Cross-job "New invoice" flow for the Invoices dashboard. Step 1 searches and
 * selects a job from existing jobs; step 2 hands off to the per-job
 * CreateInvoiceDrawer (reused as-is) to pick fees and raise the live invoice.
 */
export function NewInvoiceFlow({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (res: InvoiceCreateResponse) => void;
}) {
  const [job, setJob] = useState<JobListRow | null>(null);

  // Step 2: hand the chosen job to the existing per-job create drawer.
  if (job) {
    return (
      <CreateInvoiceDrawer
        jobId={job.id}
        leadClientName={job.client_display || null}
        defaultReference={job.job_name ?? ""}
        // Back to the job picker rather than closing the whole flow.
        onClose={() => setJob(null)}
        onSaved={onCreated}
      />
    );
  }

  return <JobPicker onClose={onClose} onPick={setJob} />;
}

function JobPicker({ onClose, onPick }: { onClose: () => void; onPick: (job: JobListRow) => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<JobListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Debounced search; an empty query lists the newest jobs so there's always
  // something to pick.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      jobsApi
        .listJobs({ q: q.trim() || undefined, per_page: 25, sort_field: "job_number", sort_direction: "desc" })
        .then((res) => {
          if (!cancelled) setResults(res.items ?? []);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not search jobs.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  return (
    <FormDrawer
      title="New invoice — choose a job"
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Cancel</Button>}
    >
      <p className="job-drawer__muted">Pick the job to bill — you'll choose the fees on the next step.</p>
      <input
        className="leads__search"
        type="search"
        placeholder="Search jobs by number, client, address, postcode…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      {loading ? (
        <div className="jobs-list__loading"><Spinner label="Searching jobs…" /></div>
      ) : results.length === 0 ? (
        <p className="job-drawer__muted">No jobs found — try a different search.</p>
      ) : (
        <ul className="job-client-results">
          {results.map((j) => (
            <li key={j.id}>
              <button type="button" className="job-client-result" onClick={() => onPick(j)}>
                <span className="job-client-result__name">
                  <span className="numeric">{j.job_number}</span> · {j.job_name || "Untitled job"}
                </span>
                <span className="job-client-result__email">
                  {j.client_display || "No client"}{j.site_full_address ? ` · ${j.site_full_address}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </FormDrawer>
  );
}
